import { ChangesStatus } from '@editor2/types/changesStatus';
import { type Chapter } from '@editor2/types/chapter';
import { type CourseState } from '@editor2/types/courseState';
import { isEqual } from 'lodash-es';

export const addStatus = (changesStatuses: ChangesStatus[], status: ChangesStatus) => {
	if (changesStatuses && changesStatuses.length) {
		return changesStatuses.includes(status) ? changesStatuses : [...changesStatuses, status];
	}

	return [status];
};

export const removeStatus = (changesStatuses: ChangesStatus[], status: ChangesStatus) => {
	if (!changesStatuses) return [];

	const index = changesStatuses.indexOf(status);
	return index >= 0
		? [...changesStatuses.slice(0, index), ...changesStatuses.slice(index + 1)]
		: changesStatuses;
};

export const isEqualIdsArrays = (arr1, arr2) => {
	if (arr1.length !== arr2.length) return false;

	const diff = arr1.filter((id, i) => arr2[i] !== id);
	return !diff.length;
};

export const deleteProperty = (obj, key: string) => {
	const { [key]: _, ...entities } = obj;

	return entities;
};

export const getChapterPages = (state: CourseState, chapter: Chapter) => {
	return chapter.page_groups
		? [].concat(
				...chapter.page_groups.map(
					(pageGroupId) => state.page_groups.entities[pageGroupId]?.pages || []
				)
		  )
		: chapter.pages;
};

export const renumberChapterPages = (state, chapter: Chapter) => {
	const chapterPages = getChapterPages(state, chapter);

	chapterPages.forEach((pageId, index) => {
		const page = state.pages.entities[pageId];
		const chapterIndex = state.chapters.ids.indexOf(chapter.family_id);

		const pageNumber = page.page_number || `Ch#${chapterIndex + 1} / Pg#${index + 1}`;

		if (!state.pages.entities[pageId].currentPageNumber) {
			state.pages.entities[pageId] = {
				...state.pages.entities[pageId],
				currentPageNumber: pageNumber
			};
		}

		state.pages.entities[pageId].currentPageNumber = pageNumber;
	});
};

export const updateBookChanges = (state: CourseState) => {
	const { metadata: _, ...initAttributes } = state.initialCourse.book;
	const { metadata, ...currentAttributes } = state.book;

	if (isEqual(initAttributes, currentAttributes))
		state.changes.toc = removeStatus(state.changes.toc, ChangesStatus.Modified);

	if (isEqual(state.initialCourse.chapters.ids, state.chapters.ids)) {
		state.changes.toc = state.changes.toc.filter(
			(status) => status !== ChangesStatus.ChildrenReorder
		);
	}
};

export const updateChapterChanges = (state: CourseState) => {
	Object.keys(state.chapters.entities).map((chapterId) => {
		const {
			page_groups: initPageGroups,
			pages: initPages,
			...initAttributes
		} = state.initialCourse.chapters.entities[chapterId];
		const {
			page_groups: currentPageGroups,
			pages: currentPages,
			...currentAttributes
		} = state.chapters.entities[chapterId];

		if (isEqual(initAttributes, currentAttributes)) {
			state.changes.chapters[chapterId] = removeStatus(
				state.changes.chapters[chapterId],
				ChangesStatus.Modified
			);
			state.changes.chapters[chapterId] = removeStatus(
				state.changes.chapters[chapterId],
				ChangesStatus.New
			);
		}

		const statuses = state.changes.chapters[chapterId];
		if (initAttributes.page_groups_enabled) {
			state.changes.chapters[chapterId] = isEqual(initPageGroups, currentPageGroups)
				? removeStatus(statuses, ChangesStatus.ChildrenReorder)
				: addStatus(statuses, ChangesStatus.ChildrenReorder);
		} else {
			state.changes.chapters[chapterId] = isEqual(initPages, currentPages)
				? removeStatus(statuses, ChangesStatus.ChildrenReorder)
				: addStatus(statuses, ChangesStatus.ChildrenReorder);
		}
	});
};

export const updatePageGroupChanges = (state: CourseState) => {
	Object.keys(state.page_groups.entities).map((pageGroupId) => {
		const { pages: initPages, ...initAttributes } =
			state.initialCourse.page_groups.entities[pageGroupId];
		const { pages: currentPages, ...currentAttributes } = state.page_groups.entities[pageGroupId];

		if (isEqual(initAttributes, currentAttributes)) {
			state.changes.page_groups[pageGroupId] = removeStatus(
				state.changes.page_groups[pageGroupId],
				ChangesStatus.Modified
			);
			state.changes.page_groups[pageGroupId] = removeStatus(
				state.changes.page_groups[pageGroupId],
				ChangesStatus.New
			);
		}

		const statuses = state.changes.page_groups[pageGroupId];
		state.changes.page_groups[pageGroupId] = isEqual(initPages, currentPages)
			? removeStatus(statuses, ChangesStatus.ChildrenReorder)
			: addStatus(statuses, ChangesStatus.ChildrenReorder);
	});
};

export const updatePageChanges = (state: CourseState) => {
	Object.keys(state.pages.entities).map((pageId) => {
		const initialPage = state.initialCourse.pages.entities[pageId];
		if (!initialPage) return;

		const { elements: initElements, ...initAttributes } = initialPage;
		const { elements: currentElements, ...currentAttributes } = state.pages.entities[pageId];

		if (isEqual(initAttributes, currentAttributes)) {
			state.changes.pages[pageId] = removeStatus(
				state.changes.pages[pageId],
				ChangesStatus.Modified
			);
			state.changes.pages[pageId] = removeStatus(state.changes.pages[pageId], ChangesStatus.New);
		}

		const statuses = state.changes.pages[pageId];
		state.changes.pages[pageId] = isEqual(initElements, currentElements)
			? removeStatus(statuses, ChangesStatus.ChildrenReorder)
			: addStatus(statuses, ChangesStatus.ChildrenReorder);
	});
};

export const updateElementChanges = (state: CourseState) => {
	Object.keys(state.elements.entities).map((elementId) => {
		const initAttributes = state.initialCourse.elements.entities[elementId];
		const currentAttributes = state.elements.entities[elementId];

		if (isEqual(initAttributes, currentAttributes)) {
			state.changes.elements[elementId] = removeStatus(
				state.changes.elements[elementId],
				ChangesStatus.Modified
			);
			state.changes.elements[elementId] = removeStatus(
				state.changes.elements[elementId],
				ChangesStatus.New
			);
		}
	});
};

export const updateCourseChanges = (state: CourseState) => {
	updateBookChanges(state);
	updateChapterChanges(state);
	updatePageGroupChanges(state);
	updatePageChanges(state);
	updateElementChanges(state);
};
